import { useMemo } from 'react';
import { useGate } from 'statsig-react';
import type { ReleaseTypeEnum } from '@soundxyz/utils/const';
import type { ContractType } from '@graphql/reactQuery';
import { FEATURE_GATES } from '@utils/constants';
import { getReleaseEditButtonUrl, getUrlToEditFlow } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const DISABLED = 'DISABLED' as const;

/*
 * Returns the path to the edit flow
 */
export function useEditPath({
  contractType,
  draftId,
  releaseId,
  releaseType,
  stepId,
}: {
  contractType: ContractType;
  draftId: string | null;
  releaseId: string;
  releaseType: ReleaseTypeEnum;
  stepId: number;
}) {
  const { value: enableEditV2 } = useGate(FEATURE_GATES.EDIT_RELEASE_V2);
  const { value: enableEditTiered } = useGate(FEATURE_GATES.EDIT_TIERED_RELEASE);

  return useMemo(() => {
    switch (contractType) {
      case 'TIERED_EDITION':
        if (enableEditTiered) {
          return { path: webappUriPath(getUrlToEditFlow({ releaseId, stepId })) };
        }

        return DISABLED;
      case 'EDITION':
        if (enableEditV2) {
          return { path: webappUriPath(getUrlToEditFlow({ releaseId, stepId })) };
        }

        if (!draftId) {
          return DISABLED;
        }

        return {
          path: webappUriPath(
            getReleaseEditButtonUrl({
              releaseType,
              draftId,
              stepId,
            }),
          ),
        };
      default:
        return DISABLED;
    }
  }, [contractType, draftId, enableEditTiered, enableEditV2, releaseId, releaseType, stepId]);
}
